import type { components } from "@/api-schema";
import { sortByRenderedLabel } from "@/common/helpers/sort-by-rendered-label";
import { cn } from "@/common/helpers/utils";
import { BrandLogo, BrandLogoVariant } from "@/components/brand-logo/brand-logo";
import { ContactLink } from "@/components/contact-link/contact-link";
import { AddressBlock } from "@/components/detail-page/_components/address-block";
import { DetailTabCard } from "@/components/detail-page/_components/tab/detail-tab-card";
import { DetailTabGrid } from "@/components/detail-page/_components/tab/detail-tab-grid";
import { DetailTabRow } from "@/components/detail-page/_components/tab/detail-tab-row";
import { RelationLink } from "@/components/relation-link/relation-link";
import { Card, CardContent } from "@/components/ui/card";
import { EmptyValue } from "@/components/ui/empty-value";
import { Building2, CarFront, MailIcon, MapPin, PhoneIcon, PrinterIcon, type LucideIcon } from "lucide-react";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";

type Branch = components["schemas"]["Branch"];

interface BrandContactItem {
  uuid: string;
  name: string;
  logoUrl?: string;
  phone?: string | null;
  fax?: string | null;
  email?: string | null;
}

export function OverviewTab({ branch }: { branch: Branch }): ReactElement {
  const { t } = useTranslation(["masterdata", "common"]);
  const address = branch.address;

  return (
    <DetailTabGrid className="xl:grid-cols-2">
      <DetailTabCard icon={Building2} title={t("masterdata:BranchInformation")}>
        <DetailTabRow label={t("common:Name")} value={branch.name} />
        <DetailTabRow
          label={t("common:Company")}
          value={
            branch.company ? (
              <RelationLink subject="Company" uuid={branch.company.uuid}>
                {branch.company.name}
              </RelationLink>
            ) : undefined
          }
        />
        <DetailTabRow
          label={t("common:Phone")}
          value={
            branch.phone ? (
              <ContactLink icon={PhoneIcon} label={branch.phone} href={`tel:${branch.phone}`} />
            ) : undefined
          }
        />
        <DetailTabRow
          label={t("common:Email")}
          value={
            branch.email ? (
              <ContactLink icon={MailIcon} label={branch.email} href={`mailto:${branch.email}`} />
            ) : undefined
          }
        />
        <DetailTabRow
          label={t("masterdata:BranchSignatureTablet")}
          value={
            branch.signatureTabletEmail ? (
              <ContactLink
                icon={MailIcon}
                label={branch.signatureTabletEmail}
                href={`mailto:${branch.signatureTabletEmail}`}
              />
            ) : undefined
          }
        />
      </DetailTabCard>

      <DetailTabCard icon={MapPin} title={t("masterdata:BranchAddress")}>
        <div className="py-1.5">
          <AddressBlock address={address ?? {}} />
        </div>
        <DetailTabRow label={t("common:Latitude")} value={address?.latitude} mono />
        <DetailTabRow label={t("common:Longitude")} value={address?.longitude} mono />
      </DetailTabCard>

      <BranchBrandsCard branch={branch} />
    </DetailTabGrid>
  );
}

function BranchBrandsCard({ branch }: { branch: Branch }): ReactElement {
  const { t } = useTranslation(["masterdata"]);
  const brandContactItems = getBrandContactItems(branch);

  return (
    <DetailTabCard icon={CarFront} title={t("masterdata:BranchBrands")} className="lg:col-span-2">
      {brandContactItems.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 pt-1.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {brandContactItems.map((item) => (
            <BranchBrandItem key={item.uuid} item={item} />
          ))}
        </div>
      ) : (
        <div className="py-1.5">
          <EmptyValue />
        </div>
      )}
    </DetailTabCard>
  );
}

function getBrandContactItems(branch: Branch): BrandContactItem[] {
  const brands = sortByRenderedLabel(
    branch.brands?.filter((brand): brand is typeof brand & { name: string } => Boolean(brand.name)) ?? [],
    (brand) => brand.name,
  );
  const contacts = branch.brandRelations ?? [];

  return brands.map((brand) => {
    const contact = contacts.find((item) => item.brand?.uuid === brand.uuid);
    return {
      uuid: brand.uuid,
      name: brand.name,
      logoUrl: brand.logoUrl,
      phone: contact?.phone,
      fax: contact?.fax,
      email: contact?.email,
    };
  });
}

function BranchBrandItem({ item }: { item: BrandContactItem }): ReactElement {
  return (
    <Card className="py-3" depth={1}>
      <CardContent className="flex flex-col gap-2 px-4">
        <div className="flex items-center gap-2">
          <BrandLogo brand={{ name: item.name, logoUrl: item.logoUrl }} variant={BrandLogoVariant.COMPACT} />
          <RelationLink subject="Brand" uuid={item.uuid}>
            {item.name}
          </RelationLink>
        </div>
        <div className="flex flex-col gap-1 text-sm">
          <BrandContactLine icon={PhoneIcon} value={item.phone} hrefPrefix="tel:" />
          <BrandContactLine icon={PrinterIcon} value={item.fax} hrefPrefix="fax:" />
          <BrandContactLine icon={MailIcon} value={item.email} hrefPrefix="mailto:" />
        </div>
      </CardContent>
    </Card>
  );
}

function BrandContactLine({
  icon: Icon,
  value,
  hrefPrefix,
}: {
  icon: LucideIcon;
  value?: string | null;
  hrefPrefix: string;
}): ReactElement {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <Icon className={cn("h-4 w-4 shrink-0", value ? "text-primary" : "text-muted-foreground")} />
      {value ? (
        <a href={`${hrefPrefix}${value}`} className="text-primary truncate hover:underline">
          {value}
        </a>
      ) : (
        <EmptyValue />
      )}
    </div>
  );
}
